import type React from 'react';
import { useState } from 'react';
import type { CodCheckoutInputDto, CodFieldValue, CodFormConfig } from '@xshop/shared';
import { submitCodOrder } from '../api/client';
import { trackingAllowed } from '../tracking/tracker';
import { pixelTrackingPayload } from '../tracking/pixels';
import { trackPlaced } from '../commerce/commerceEvents';
import { holdCardOrder } from '../commerce/cardPayment';
import { serverFieldErrors, validateCodValues } from './codFormValidation';
import { codControlId } from './CodFormFields';
import { refusedDiscount } from './refusedDiscount';
import { CHECKOUT_COUNTRY } from './useCheckoutMoney';
import type { CheckoutLabels } from './checkoutLabels';
import type { CheckoutPayment } from './PaymentChoice';

// Placing the COD form's order: the answers are checked against the merchant's config first (the API checks them
// again), then sent under their storage keys; a card order leaves for Stripe's page, a cash one is confirmed here.

/** What the order is made of, as the form holds it. */
export type CheckoutOrder = {
  lineItems: CodCheckoutInputDto['lineItems'];
  acceptedOfferId?: string;
  discountCode?: string;
  orderNote?: string;
  payment: CheckoutPayment;
};

/** Everything placing the order reads, and the ways it reports back. */
export type SubmitInput = {
  config: CodFormConfig | null;
  /** True in the builder and a theme preview: the form can be filled in to see it work, but never sends. */
  preview: boolean;
  text: CheckoutLabels;
  values: Readonly<Record<string, CodFieldValue>>;
  setErrors: (errors: Record<string, string>) => void;
  order: CheckoutOrder;
  /** Refuses the applied code when the server did, and the name the summary showed the discount under. */
  discount: { refuse: (refusal: NonNullable<ReturnType<typeof refusedDiscount>>) => void; label: string | null };
  pixels: { contents: Array<{ id: string; quantity: number }>; currency: string };
  onSuccess: (orderData: unknown) => void;
};

/** What sending the form says in a preview, where there is no shop's order to place. */
const PREVIEW_NOTE = 'This is a preview. Orders are placed on your live store.';

// Puts the keyboard in the first field a submission was refused for, so the shopper never hunts for it.
const focusFirstRefused = (found: Readonly<Record<string, string>>): void => {
  const first = Object.keys(found).find((key) => found[key]);
  if (first) document.getElementById(codControlId(first))?.focus();
};

// Builds what the API is sent: the lines, the bump's id (the server prices it from its own row), the code, the answers
// under their keys, the note, how it is paid, and the pixels' ids when the shopper allows tracking.
const orderPayload = (order: CheckoutOrder, values: SubmitInput['values']): CodCheckoutInputDto => ({
  lineItems: order.lineItems,
  acceptedUpsellOfferId: order.acceptedOfferId,
  discountCode: order.discountCode,
  codFields: { ...values, country: CHECKOUT_COUNTRY },
  orderNote: order.orderNote?.trim() || undefined,
  paymentMethod: order.payment,
  pixelTracking: trackingAllowed() ? pixelTrackingPayload() : undefined
});

// Sends the order and hands it on: a card order to Stripe's page (a purchase only once the shopper is back), a cash
// one to the thank-you page, carrying the discount's name the server's order does not.
async function placeOrder(input: SubmitInput): Promise<void> {
  const placed = await submitCodOrder(orderPayload(input.order, input.values));
  if (placed?.payment?.url) {
    holdCardOrder({ placed, contents: input.pixels.contents, currency: input.pixels.currency, sessionId: placed.payment.sessionId });
    return window.location.assign(placed.payment.url);
  }
  trackPlaced(placed, input.pixels.contents, input.pixels.currency);
  input.onSuccess({ ...placed, discountLabel: input.discount.label });
}

// Gives the form its submit: checking, sending, and saying what went wrong, field by field and above the form.
export function useCheckoutSubmit(input: SubmitInput) {
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Marks the answers the config refuses, focusing the first; true when every one passes.
  const passes = (config: CodFormConfig): boolean => {
    const found = validateCodValues(config, input.values, CHECKOUT_COUNTRY, input.text);
    input.setErrors(found);
    if (Object.keys(found).length === 0) return true;
    focusFirstRefused(found);
    setFormError(input.text.invalidFields);
    return false;
  };

  // Says why the server refused: the code it turned down, the fields it named, and its message above the form.
  const refused = (err: unknown) => {
    const codeRefusal = refusedDiscount(err);
    if (codeRefusal) input.discount.refuse(codeRefusal);
    const fields = serverFieldErrors(err);
    input.setErrors(fields);
    focusFirstRefused(fields);
    setFormError((err as { message?: string } | null)?.message || input.text.submitFailed);
  };

  // Submits the answers under their storage keys, once they pass.
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (input.preview) return setFormError(PREVIEW_NOTE);
    setFormError(null);
    if (!input.config || !passes(input.config)) return;
    setSubmitting(true);
    try {
      await placeOrder(input);
    } catch (err) {
      refused(err);
    } finally {
      setSubmitting(false);
    }
  };

  return { submit, submitting, formError };
}
